"use client";
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';

import { Phone, Mail, Clock, Download, Globe, MapPin } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { siteConfig } from '@/data/siteConfig'
import { useCataloguePDF } from '@/lib/catalogue/useCataloguePDF'

export function Footer() {
  const pathname = usePathname();
  const { generateFullCatalogue, isGenerating } = useCataloguePDF()

  if (pathname.startsWith('/admin')) {
    return null;
  }

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          <div className="flex flex-col gap-4 md:col-span-1">
            <Link href="/" className="inline-block">
              <Image 
                src="/logo-v2-light.svg" 
                alt="Jamnagar Metals" 
                width={180}
                height={48}
                className="h-12 w-auto opacity-90 hover:opacity-100 transition-opacity" 
              />
            </Link>
            <p className="text-sm text-primary-foreground/60 leading-relaxed max-w-xs">
              {siteConfig.tagline} {siteConfig.natureOfBusiness} based in {siteConfig.location}.
            </p>
            <div className="flex flex-col gap-2 mt-1">
              <a href={`tel:${siteConfig.phone}`} className="flex items-center gap-2 text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                {siteConfig.phone}
              </a>
              <a href={`mailto:${siteConfig.email}`} className="flex items-center gap-2 text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                {siteConfig.email}
              </a>
              <div className="flex items-center gap-2 text-sm text-primary-foreground/60">
                <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                {siteConfig.workingHours}
              </div>
              <div className="flex items-center gap-2 text-sm text-primary-foreground/60">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                {siteConfig.export.portOfLoading}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/60">Products</h3>
            <nav className="flex flex-col gap-1.5">
              <Link href="/tower-bolts" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">Tower Bolts</Link>
              <Link href="/cabinet-handles" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">Cabinet Handles</Link>
              <button
                onClick={generateFullCatalogue}
                disabled={isGenerating}
                className="flex items-center gap-1.5 text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors w-fit mt-1"
              >
                <Download className="h-3.5 w-3.5" />
                {isGenerating ? 'Preparing...' : 'Download Full Catalogue'}
              </button>
            </nav>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/60 mt-4">Company</h3>
            <nav className="flex flex-col gap-1.5">
              <Link href="/about" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">About Us</Link>
              <Link href="/export" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">Export</Link>
              <Link href="/contact" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">Contact</Link>
              <Link href="/quick-inquiry" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">Quick Inquiry</Link>
              <Link href="/quote" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">Request a Quote</Link>
            </nav>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/60">Export Desk</h3>
            <div className="flex flex-col gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-primary-foreground/60 mb-1">Incoterms</p>
                <p className="text-sm text-primary-foreground/80">{siteConfig.export.incoterms.join(' · ')}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-primary-foreground/60 mb-1">Currencies</p>
                <p className="text-sm text-primary-foreground/80">{siteConfig.export.currencies.join(' · ')}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-primary-foreground/60 mb-1">Lead Time</p>
                <p className="text-sm text-primary-foreground/80">{siteConfig.export.leadTime}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-primary-foreground/60">Credentials</h3>
            <div className="flex flex-wrap gap-1.5">
              {siteConfig.export.certifications.map((label) => (
                <Badge key={label} variant="outline" className="text-[10px] font-medium border-primary-foreground/20 text-primary-foreground/80 bg-transparent">
                  {label}
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2 text-sm text-primary-foreground/60">
              <Globe className="h-3.5 w-3.5" />
              <span>Serving 30+ countries</span>
            </div>
            <p className="text-xs text-primary-foreground/60 leading-relaxed mt-2">
              {siteConfig.bulkOrderNote}
            </p>
          </div>
        </div>

        <Separator className="bg-primary-foreground/20 mb-6" />

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-primary-foreground/60">
          <p>{siteConfig.legal.brandStatement}</p>
          <nav className="flex flex-wrap gap-x-4 gap-y-1">
            <Link href="/privacy-policy" className="hover:text-primary-foreground transition-colors">Privacy</Link>
            <Link href="/terms-and-conditions" className="hover:text-primary-foreground transition-colors">Terms</Link>
            <Link href="/shipping-policy" className="hover:text-primary-foreground transition-colors">Shipping</Link>
            <Link href="/refund-policy" className="hover:text-primary-foreground transition-colors">Refund</Link>
          </nav>
          <p className="flex-shrink-0">© {new Date().getFullYear()} Jamnagar Metals</p>
        </div>
      </div>
    </footer>
  )
}
